import { ExternalLink, Link2, Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { SeveridadeBadge, StatusIncidenteBadge } from '@/components/patterns/incidente-badges';
import { PermissionButton } from '@/components/patterns/permission-button';
import { SearchSelect, type OpcaoBusca } from '@/components/patterns/search-select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { BuildDetalhe, IncidenteResumo } from '@/data/types';
import { useSessao } from '@/hooks/use-sessao';
import { emitir } from '@/lib/eventos';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { podeEscreverIncidente, ROTULO_SEVERIDADE } from '@/lib/incidentes';
import { cn } from '@/lib/utils';
import { IncidenteDialog } from './IncidenteDialog';
import { IncidenteSheet } from './IncidenteSheet';
import { rotuloDoLink } from './rotulo-link';

/**
 * "Incidentes conhecidos" na tela de detalhe da versão: lista (abertos e
 * mitigados primeiro — a ordem já vem assim da API), registrar um novo com a
 * versão pré-preenchida e associar um incidente existente. Clicar abre o
 * detalhe do incidente por cima da versão.
 */
export function IncidentesDaVersao({ build }: { build: BuildDetalhe }) {
  const { roles } = useSessao();
  const escrita = podeEscreverIncidente(roles);
  const [registrando, setRegistrando] = React.useState(false);
  const [associando, setAssociando] = React.useState(false);
  const [aberto, setAberto] = React.useState<number | null>(null);

  const naoResolvidos = build.incidentes.filter((item) => item.status !== 'resolvido').length;

  return (
    <section aria-labelledby="titulo-incidentes-versao" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id="titulo-incidentes-versao" className="text-section-title">
          Incidentes conhecidos
        </h2>
        <span className="cmp-eyebrow">
          {build.incidentes.length === 0
            ? 'nenhum registrado'
            : `${plural(build.incidentes.length, 'incidente', 'incidentes')} · ${naoResolvidos} em aberto`}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <PermissionButton
            permitido={escrita.permitido}
            motivo={escrita.motivo}
            size="sm"
            variant="outline"
            onClick={() => setAssociando(true)}
          >
            <Link2 aria-hidden="true" />
            Associar incidente existente
          </PermissionButton>
          <PermissionButton
            permitido={escrita.permitido}
            motivo={escrita.motivo}
            size="sm"
            variant="outline"
            onClick={() => setRegistrando(true)}
          >
            <Plus aria-hidden="true" />
            Registrar incidente
          </PermissionButton>
        </div>
      </div>

      {build.incidentes.length === 0 ? (
        <p className="rounded-input border border-dashed border-border-strong bg-bg-subtle px-4 py-5 text-body text-on-surface-muted">
          Nenhum incidente conhecido para esta versão. Se ela tiver um bug, registre um incidente: quem for homologar
          será avisado e o feed destaca a versão.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {build.incidentes.map((incidente) => (
            <li key={incidente.id}>
              <LinhaIncidente incidente={incidente} aoAbrir={() => setAberto(incidente.id)} />
            </li>
          ))}
        </ul>
      )}

      <IncidenteDialog
        aberto={registrando}
        versoesIniciais={[build.versao]}
        aoFechar={() => setRegistrando(false)}
        aoSalvar={(detalhe) => {
          setRegistrando(false);
          setAberto(detalhe.id);
        }}
      />

      <AssociarIncidenteDialog build={build} aberto={associando} aoFechar={() => setAssociando(false)} />

      <IncidenteSheet id={aberto} aoFechar={() => setAberto(null)} />
    </section>
  );
}

function LinhaIncidente({ incidente, aoAbrir }: { incidente: IncidenteResumo; aoAbrir: () => void }) {
  const resolvido = incidente.status === 'resolvido';
  return (
    <div className={cn('flex flex-col gap-1.5 rounded-input border border-border bg-bg-surface px-3 py-2.5', resolvido && 'bg-bg-subtle')}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <SeveridadeBadge severidade={incidente.severidade} />
        <StatusIncidenteBadge status={incidente.status} />
        <button
          type="button"
          onClick={aoAbrir}
          aria-label={`Abrir o incidente ${incidente.id}: ${incidente.titulo}`}
          className={cn(
            'min-w-0 flex-1 basis-56 rounded-button text-left text-body font-bold underline-offset-4 hover:text-primary hover:underline',
            resolvido && 'font-normal text-on-surface-muted',
          )}
        >
          {incidente.titulo}
        </button>
      </div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-cell text-on-surface-muted">
        <span title={formatarDataHora(incidente.criado_em)}>
          #{incidente.id} · registrado por {incidente.criado_por} {formatarRelativo(incidente.criado_em)}
        </span>
        {incidente.link ? <span aria-hidden="true">·</span> : null}
        {incidente.link ? (
          <a
            href={incidente.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="size-3.5" aria-hidden="true" />
            {rotuloDoLink(incidente.link)}
            <span className="sr-only">(ticket, abre em nova aba)</span>
          </a>
        ) : null}
        {incidente.corrigido_em ? <span aria-hidden="true">·</span> : null}
        {incidente.corrigido_em ? (
          <span>
            corrigido em <span className="font-mono text-on-surface">{incidente.corrigido_em}</span>
          </span>
        ) : null}
      </p>
    </div>
  );
}

function AssociarIncidenteDialog({
  build,
  aberto,
  aoFechar,
}: {
  build: BuildDetalhe;
  aberto: boolean;
  aoFechar: () => void;
}) {
  const [candidatos, setCandidatos] = React.useState<IncidenteResumo[] | null>(null);
  const [erroCarga, setErroCarga] = React.useState<string | null>(null);
  const [escolhido, setEscolhido] = React.useState<string | null>(null);
  const [erro, setErro] = React.useState<string | null>(null);
  const [enviando, setEnviando] = React.useState(false);

  const jaAssociados = React.useMemo(() => new Set(build.incidentes.map((item) => item.id)), [build.incidentes]);

  React.useEffect(() => {
    if (!aberto) return;
    const controller = new AbortController();
    setCandidatos(null);
    setErroCarga(null);
    setEscolhido(null);
    setErro(null);
    dataSource
      .listarIncidentes({ produto: build.produto, status: ['aberto', 'mitigado'], limit: 100 }, null, controller.signal)
      .then((pagina) => setCandidatos(pagina.items))
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        setErroCarga(mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa)));
      });
    return () => controller.abort();
  }, [aberto, build.produto]);

  const opcoes: OpcaoBusca[] = (candidatos ?? [])
    .filter((item) => !jaAssociados.has(item.id))
    .map((item) => ({
      valor: String(item.id),
      rotulo: `#${item.id} ${item.titulo}`,
      detalhe: ROTULO_SEVERIDADE[item.severidade],
    }));

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!escolhido) return;
    setEnviando(true);
    setErro(null);
    try {
      const detalhe = await dataSource.adicionarVersoesAoIncidente(Number(escolhido), [build.versao]);
      emitir({ tipo: 'incidente', id: detalhe.id, versoes: detalhe.versoes.map((v) => v.versao) });
      toast.success(`Incidente #${detalhe.id} associado à versão ${build.versao}.`);
      aoFechar();
    } catch (causa) {
      setErro(mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa)));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && !enviando && aoFechar()}>
      <DialogContent>
        <form onSubmit={enviar} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Associar incidente existente</DialogTitle>
            <DialogDescription>
              Escolha um incidente em aberto ou mitigado que também afeta a versão {build.versao}.
            </DialogDescription>
          </DialogHeader>

          {erroCarga ? (
            <Callout tom="erro">Não foi possível listar os incidentes: {erroCarga}</Callout>
          ) : (
            <Field
              id="associar-incidente"
              label="Incidente"
              obrigatorio
              apoio={
                candidatos === null
                  ? 'Carregando incidentes…'
                  : opcoes.length === 0
                    ? 'Não há incidente não resolvido que ainda não esteja associado a esta versão.'
                    : `${opcoes.length} ${opcoes.length === 1 ? 'incidente disponível' : 'incidentes disponíveis'}.`
              }
            >
              {(props) => (
                <SearchSelect
                  id={props.id}
                  describedBy={props['aria-describedby']}
                  valor={escolhido}
                  aoMudar={setEscolhido}
                  opcoes={opcoes}
                  disabled={candidatos === null || opcoes.length === 0}
                  placeholder="Escolher um incidente"
                  placeholderBusca="Buscar pelo título…"
                  vazio="Nenhum incidente com este título."
                />
              )}
            </Field>
          )}

          {erro ? <Callout tom="erro">{erro}</Callout> : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={enviando} disabled={!escolhido}>
              Associar à versão
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
